import { Button, Select } from "antd";
import { Theme } from "api/config/chalice-types";
import ResponsiveModal from "components/ResponsiveModal/ResponsiveModal";
import { TemplateCounts } from "pages/Themes/useTemplateCounts";
import ThemeCard from "./ThemeCard";
import useFilteredSortedThemes from "./useFilteredSortedThemes";

interface ThemeSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedThemes: string[];
  setSelectedThemes: (selected: string[]) => void;
  allThemes: Theme[];
  allThemesLoading: boolean;
  onSave: () => Promise<void>;
  templateCounts: TemplateCounts;
  isLoadingCounts: boolean;
}

const ThemeSelectionModal = ({
  isOpen,
  onClose,
  selectedThemes,
  setSelectedThemes,
  allThemes,
  allThemesLoading,
  onSave,
  templateCounts,
  isLoadingCounts,
}: ThemeSelectionModalProps) => {
  const {
    filteredSortedThemes,
    sortOption,
    setSortOption,
    selectedCategories,
    setSelectedCategories,
    allCategories,
    selectedStyles,
    setSelectedStyles,
    allStyles,
  } = useFilteredSortedThemes({
    allThemes,
    allThemesLoading,
    selectedThemes,
  });

  const handleSelect = (themeName: string) => {
    const nextSelectedThemes = selectedThemes.includes(themeName)
      ? selectedThemes.filter((name) => name !== themeName)
      : [...selectedThemes, themeName];
    setSelectedThemes(nextSelectedThemes);
  };

  return (
    <ResponsiveModal
      title="Edit Themes"
      open={isOpen}
      onCancel={onClose}
      footer={
        <div className="flex justify-between items-center w-full">
          <span className="text-base text-antd-colorTextSecondary mr-4">
            {selectedThemes.length} themes selected
          </span>
          <div className="flex items-center gap-2">
            <Button key="back" onClick={onClose}>
              Cancel
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={onSave}
              disabled={!selectedThemes.length}
            >
              Save and Close
            </Button>
          </div>
        </div>
      }
    >
      <p className="mb-4 text-antd-colorTextSecondary">
        Select the themes you want to use for post generation.
      </p>
      <div className="flex flex-wrap gap-4 mb-4 items-center">
        <div className="flex items-center gap-2">
          <label htmlFor="theme-sort" className="text-sm font-medium">
            Sort by:
          </label>
          <Select
            id="theme-sort"
            value={sortOption}
            onChange={setSortOption}
            style={{ width: 150 }}
            options={[
              { value: "release-date", label: "Newest" },
              { value: "name", label: "Name" },
              { value: "selected", label: "Selected" },
            ]}
          />
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="theme-category" className="text-sm font-medium">
            Category:
          </label>
          <Select
            id="theme-category"
            mode="multiple"
            allowClear
            style={{ minWidth: 150 }}
            placeholder="Select categories..."
            value={selectedCategories}
            onChange={setSelectedCategories}
            options={allCategories.map((cat) => ({
              label: cat,
              value: cat,
            }))}
          />
        </div>

        {allStyles.length > 0 && (
          <div className="flex items-center gap-2">
            <label htmlFor="theme-style" className="text-sm font-medium">
              Style:
            </label>
            <Select
              id="theme-style"
              mode="multiple"
              allowClear
              style={{ minWidth: 150 }}
              placeholder="Select styles..."
              value={selectedStyles}
              onChange={setSelectedStyles}
              options={allStyles.map((style) => ({
                label: style,
                value: style,
              }))}
            />
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 p-3">
        {filteredSortedThemes.map((theme) => (
          <ThemeCard
            isEditing={true}
            theme={theme}
            key={theme.name}
            handleSelect={handleSelect}
            selectedThemes={selectedThemes}
            templateCount={templateCounts[theme.name]}
            isLoadingCount={isLoadingCounts}
          />
        ))}
      </div>
    </ResponsiveModal>
  );
};

export default ThemeSelectionModal;
